import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { sendVerificationEmail, initiatePasswordReset, changeEmailAddress, isEmailVerified, reauthenticateUser } from '../../services/authService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import CustomButton from '../common/CustomButton';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { 
  Loader2, 
  MailIcon, 
  Shield,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const LoadingSkeleton = () => (
  <div className="space-y-6">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
        <div className="flex items-center justify-between">
          <div className="space-y-2 flex-1">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
          </div>
          <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
        </div>
      </div>
    ))}
  </div>
);

// Add an Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AccountManagement component error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 animate-fade-in">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Account Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage your account security and email settings
              </p>
            </div>
            <Card className="rounded-2xl border border-red-200 dark:border-red-800 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-5 w-5" />
                  Something went wrong
                </CardTitle>
                <CardDescription>
                  There was an error loading the Account Management page
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 rounded-xl mb-4">
                  <AlertDescription>
                    {this.state.error && this.state.error.toString()}
                  </AlertDescription>
                </Alert>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-3 bg-red-600 text-white rounded-full font-medium hover:bg-red-700 transition-colors duration-200"
                >
                  Try Again
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AccountManagement = () => {
  console.log("AccountManagement component initializing...");
  const { currentUser, refreshUser } = useAuth();
  const { isDarkMode, primaryColor, secondaryColor } = useTheme();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isEmailChangeDialogOpen, setIsEmailChangeDialogOpen] = useState(false);
  const [functionsAvailable, setFunctionsAvailable] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if Firebase Functions are available
    const checkFunctions = async () => {
      try {
        setInitialLoading(true);
        // Try to create a callable function - this will fail if Firebase Functions are not available
        console.log("Checking Firebase Functions availability with:", functions);
        
        // Check if functions is undefined or null
        if (!functions) {
          console.error('Firebase Functions object is undefined or null');
          setFunctionsAvailable(false);
          setMessage({
            type: 'error',
            text: 'Firebase Functions could not be initialized. Please try again later.'
          });
          return;
        }
        
        // Attempt to create a callable function
        try {
          const testFunction = httpsCallable(functions, "sendPasswordReset");
          console.log("Firebase Functions callable created successfully:", testFunction);
          setFunctionsAvailable(true);
        } catch (callableError) {
          console.error('Firebase Functions callable creation failed:', callableError);
          setFunctionsAvailable(false);
          setMessage({
            type: 'error',
            text: 'Email functions are currently unavailable. Please try again later.'
          });
        }
      } catch (error) {
        console.error('Firebase Functions check failed with unexpected error:', error);
        setFunctionsAvailable(false);
        setMessage({
          type: 'error',
          text: 'Email functions are currently unavailable due to a system error. Please try again later.'
        });
      } finally {
        setInitialLoading(false);
      }
    };
    
    // Call the check function
    checkFunctions();
  }, []);

  const handleSendVerificationEmail = async () => {
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.'
      });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      await sendVerificationEmail();
      setMessage({
        type: 'success',
        text: 'Verification email sent! Check your inbox and spam folder.'
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      setMessage({
        type: 'error',
        text: `Failed to send verification email: ${error.message || 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.'
      });
      return;
    }

    if (!currentUser?.email) {
      setMessage({
        type: 'error',
        text: 'No email associated with your account.'
      });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      await initiatePasswordReset(currentUser.email);
      setMessage({
        type: 'success',
        text: 'Password reset email sent! Check your inbox and spam folder.'
      });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setMessage({
        type: 'error',
        text: `Failed to send password reset email: ${error.message || 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.'
      });
      return;
    }
    
    if (!newEmail || !currentPassword) {
      setMessage({
        type: 'error',
        text: 'Please fill in all fields.'
      });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // First reauthenticate the user
      await reauthenticateUser(currentPassword);
      
      // Then change the email
      await changeEmailAddress(newEmail);
      
      setMessage({
        type: 'success',
        text: 'Email change initiated! Check your new email for verification.'
      });
      
      // Reset form and close dialog
      setNewEmail('');
      setCurrentPassword('');
      setIsEmailChangeDialogOpen(false);
      
      // Refresh user data
      await refreshUser();
    } catch (error) {
      console.error('Error changing email:', error);
      if (error.code === 'auth/wrong-password') {
        setMessage({
          type: 'error',
          text: 'Current password is incorrect.'
        });
      } else if (error.code === 'auth/email-already-in-use') {
        setMessage({
          type: 'error',
          text: 'This email is already in use by another account.'
        });
      } else if (error.code === 'auth/invalid-email') {
        setMessage({
          type: 'error',
          text: 'Please enter a valid email address.'
        });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to initiate email change: ${error.message || 'Unknown error'}`
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 animate-fade-in">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-80 animate-pulse"></div>
          </div>
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <button
            onClick={() => navigate('/profile')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 mb-4"
          >
            Back to Profile
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Account Management
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your account security and email settings
          </p>
        </div>

        {/* Status Alert */}
        {message.text && (
          <Alert 
            className="mb-6 rounded-2xl border shadow-sm animate-fade-in animate-stagger-1"
            style={{
              borderColor: message.type === 'success' ? primaryColor : '#ef4444',
              backgroundColor: message.type === 'success' 
                ? (isDarkMode ? `${primaryColor}20` : `${primaryColor}10`)
                : (isDarkMode ? '#ef444420' : '#ef444410'),
              color: message.type === 'success' ? primaryColor : '#ef4444'
            }}
          >
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4" style={{ color: primaryColor }} />
            ) : (
              <AlertCircle className="h-4 w-4" style={{ color: '#ef4444' }} />
            )}
            <AlertTitle>{message.type === 'success' ? 'Success' : 'Error'}</AlertTitle>
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {/* Functions Unavailable Warning */}
        {!functionsAvailable && (
          <Alert className="mb-6 rounded-2xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 animate-fade-in animate-stagger-1">
            <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            <AlertTitle className="text-orange-800 dark:text-orange-200">Service Notice</AlertTitle>
            <AlertDescription className="text-orange-700 dark:text-orange-300">
              Email functions are currently unavailable. Some features may not work properly.
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="space-y-6">
          {/* Account Security Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden animate-fade-in animate-stagger-2">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Shield className="h-5 w-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Account Security</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Manage your email verification and password settings
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Email Verification */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isEmailVerified() 
                      ? 'bg-green-100 dark:bg-green-900/30' 
                      : 'bg-orange-100 dark:bg-orange-900/30'
                  }`}>
                    {isEmailVerified() ? (
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <MailIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Email Verification</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {isEmailVerified() 
                        ? 'Your email is verified' 
                        : 'Verify your email to access all features'}
                    </p>
                  </div>
                </div>
                {!isEmailVerified() && (
                  <button
                    onClick={handleSendVerificationEmail}
                    disabled={loading || !functionsAvailable}
                    className="px-4 py-2.5 text-white rounded-full font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: primaryColor }}
                    onMouseEnter={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.backgroundColor = `${primaryColor}dd`;
                        e.target.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = primaryColor;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    {loading ? 'Sending...' : 'Verify Email'}
                  </button>
                )}
              </div>

              {/* Password Reset */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <MailIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Password Reset</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Reset your password if you've forgotten it or want to change it
                    </p>
                  </div>
                </div>
                <button
                  onClick={handlePasswordReset}
                  disabled={loading || !functionsAvailable}
                  className="px-4 py-2.5 text-white rounded-full font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                  onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                      e.target.style.backgroundColor = `${primaryColor}dd`;
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = primaryColor;
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {loading ? 'Sending...' : 'Reset Password'}
                </button>
              </div>

              {/* Change Email */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <MailIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Change Email</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Update your email address. You'll need to verify the new email
                    </p>
                  </div>
                </div>
                <Dialog open={isEmailChangeDialogOpen} onOpenChange={setIsEmailChangeDialogOpen}>
                  <DialogTrigger asChild>
                    <button
                      disabled={!functionsAvailable}
                      className="px-4 py-2.5 border rounded-full font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        borderColor: primaryColor,
                        color: primaryColor
                      }}
                      onMouseEnter={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.backgroundColor = `${primaryColor}15`;
                          e.target.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      Change Email
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">Change Email Address</DialogTitle>
                      <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                        Enter your new email address and current password to confirm the change.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleEmailChange} className="space-y-6 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="newEmail" className="text-sm font-medium text-gray-700 dark:text-gray-300">New Email Address</Label>
                        <div className="relative">
                          <MailIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="newEmail"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter your new email"
                            className="pl-10 h-12 rounded-xl border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Password</Label>
                        <div className="relative">
                          <Input
                            id="currentPassword"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter your current password"
                            className="h-12 rounded-xl border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors"
                            required
                          />
                        </div>
                      </div>
                      
                      <DialogFooter className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setIsEmailChangeDialogOpen(false)}
                          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-full font-medium transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading || !newEmail || !currentPassword || !functionsAvailable}
                          className="px-6 py-3 text-white rounded-full font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: primaryColor }}
                          onMouseEnter={(e) => {
                            if (!e.target.disabled) {
                              e.target.style.backgroundColor = `${primaryColor}dd`;
                              e.target.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = primaryColor;
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          {loading ? 'Processing...' : 'Confirm Change'}
                        </button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap the component with the ErrorBoundary
const AccountManagementWithErrorBoundary = () => (
  <ErrorBoundary>
    <AccountManagement />
  </ErrorBoundary>
);

export default AccountManagementWithErrorBoundary;
